import { LOCATION_MODE } from '@/types/location-mode';
import React, { useEffect, useRef, type ReactNode } from 'react';
import { View } from 'react-native';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';

import { ControlledInput } from '@/components/forms/controlled-input';
import { ServiceAddressField } from '@/components/services/service-address-field';
import { ChoiceCard } from '@/components/services/wizard/choice-card';
import { DurationField } from '@/components/services/wizard/duration-field';
import {
  ControlledOptionRows,
  ControlledStepperRow,
  ControlledSwitchRow,
} from '@/components/services/wizard/field';
import { PaymentMethodsField } from '@/components/services/wizard/payment-methods-field';
import { PriceAmountField } from '@/components/services/wizard/price-amount-field';
import {
  applySaleMode,
  SALE_MODE,
  SALE_MODE_OPTIONS,
  saleModeOf,
} from '@/components/services/wizard/sale-mode';
import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import type { ServiceFormData } from '@/lib/validators/service';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { usePlanFeature } from '@/hooks/use-plan-feature';

const PRICE_DISPLAY_OPTIONS = [
  {
    value: PRICE_DISPLAY_MODE.SHOW,
    label: 'Mostrar o valor',
    meaning: 'O número aparece no cartão do serviço.',
  },
  {
    value: PRICE_DISPLAY_MODE.FROM,
    label: 'A partir de',
    meaning: 'O valor aparece como mínimo, sujeito a ajuste.',
  },
  {
    value: PRICE_DISPLAY_MODE.HIDDEN,
    label: 'Sob consulta',
    meaning: 'Nenhum número na página; o cliente pergunta.',
  },
];

const LOCATION_MODE_OPTIONS = [
  {
    value: LOCATION_MODE.PROVIDER_SPACE,
    label: 'No meu espaço',
    meaning: 'O cliente vem até você. Ele vê o endereço e abre no mapa.',
  },
  {
    value: LOCATION_MODE.CLIENT_ADDRESS,
    label: 'Vou até o cliente',
    meaning: 'O endereço é combinado a cada atendimento.',
  },
  {
    value: LOCATION_MODE.ONLINE,
    label: 'Online',
    meaning: 'Chamada de vídeo ou atendimento remoto.',
  },
];

const QUOTA_PERIOD_OPTIONS = [
  {
    value: QUOTA_PERIOD.WEEK,
    label: 'Toda semana',
    meaning: 'Conta de domingo a sábado.',
  },
  {
    value: QUOTA_PERIOD.MONTH,
    label: 'Todo mês',
    meaning: 'Conta do dia 1 ao último do mês.',
  },
];

interface StepHeadingProps {
  title: string;
  subtitle: string;
}

function StepHeading({ title, subtitle }: StepHeadingProps) {
  return (
    <View className="gap-1 px-1">
      <ThemedText className="font-semibold text-[22px] leading-7 text-neutral-900">
        {title}
      </ThemedText>
      <ThemedText className="text-[13px] leading-5 text-neutral-500">{subtitle}</ThemedText>
    </View>
  );
}

/** Card branco que agrupa os controles de um passo, sem título repetido. */
function StepCard({ children }: { children: ReactNode }) {
  return (
    <View
      className="gap-1 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      {children}
    </View>
  );
}

interface StepProps {
  control: Control<ServiceFormData>;
}

/* ─────────────────────────  1. modelo de venda  ───────────────────────── */

export function StepSaleMode({
  control,
  setValue,
}: StepProps & {
  setValue: UseFormSetValue<ServiceFormData>;
}) {
  const isSubscription = useWatch({ control, name: 'isSubscription' });
  const mode = saleModeOf({ isSubscription });
  const subscriptionPlan = usePlanFeature('SUBSCRIPTIONS');

  return (
    <View className="gap-4">
      <StepHeading
        title="Como o cliente compra?"
        subtitle="É a única escolha que muda todo o resto. Dá para trocar depois."
      />
      <View className="gap-2.5">
        {SALE_MODE_OPTIONS.map((option) => (
          <ChoiceCard
            key={option.value}
            title={option.title}
            description={
              option.value === SALE_MODE.SUBSCRIPTION && subscriptionPlan.locked
                ? `${option.description} Faz parte dos planos pagos.`
                : option.description
            }
            example={option.example}
            selected={mode === option.value}
            onPress={() => {
              if (option.value === SALE_MODE.SUBSCRIPTION && !subscriptionPlan.guard()) return;
              applySaleMode(setValue, option.value);
            }}
            testID={`service-wizard-mode-${option.value}`}
          />
        ))}
      </View>
    </View>
  );
}

/* ────────────────────────────  2. preço  ─────────────────────────────── */

export function StepPrice({
  control,
  setValue,
}: StepProps & {
  setValue: UseFormSetValue<ServiceFormData>;
}) {
  const isSubscription = useWatch({ control, name: 'isSubscription' });
  const price = useWatch({ control, name: 'defaultPrice' });
  const display = useWatch({ control, name: 'priceDisplayMode' });

  const underConsultation = display === PRICE_DISPLAY_MODE.HIDDEN;

  // "Sob consulta" e um número na tela são a mesma resposta dita de dois jeitos:
  // escolher esconder apaga o valor, e voltar atrás devolve o que estava lá.
  const stashed = useRef('');
  useEffect(() => {
    if (underConsultation) {
      if (price?.trim()) {
        stashed.current = price;
        setValue('defaultPrice', '', { shouldDirty: true });
      }
      return;
    }
    if (!price?.trim() && stashed.current) {
      setValue('defaultPrice', stashed.current, { shouldDirty: true });
      stashed.current = '';
    }
  }, [underConsultation, price, setValue]);

  // Assinatura é sempre mensal: o cliente paga todo mês e marca dentro da cota.
  const unit = isSubscription ? '/mês' : '/sessão';

  return (
    <View className="gap-4">
      <StepHeading
        title="Quanto custa"
        subtitle={
          isSubscription
            ? 'O cliente é cobrado automaticamente todo mês, sem você lembrar.'
            : 'O cliente paga a sessão que marcar. Sem cobrança recorrente.'
        }
      />

      {/* A vitrine decide o valor: primeiro como o preço aparece, depois qual é. */}
      <StepCard>
        <ControlledOptionRows
          control={control}
          name="priceDisplayMode"
          title="Na página pública"
          options={PRICE_DISPLAY_OPTIONS}
          testID="service-wizard-price-display"
        />
        <View className="mt-1">
          {underConsultation ? (
            <ThemedText className="py-4 text-center text-[13px] leading-5 text-neutral-500">
              Sem valor na página. Você combina o preço com cada cliente antes de cobrar.
            </ThemedText>
          ) : (
            <PriceAmountField control={control} unit={unit} />
          )}
        </View>
      </StepCard>

      <StepCard>
        <PaymentMethodsField control={control} />
      </StepCard>
    </View>
  );
}

/* ────────────────────────────  3. agenda  ────────────────────────────── */

export function StepAgenda({
  control,
  setValue,
}: StepProps & {
  setValue: UseFormSetValue<ServiceFormData>;
}) {
  const isSubscription = useWatch({ control, name: 'isSubscription' });
  const durationMinutes = useWatch({ control, name: 'durationMinutes' });
  const locationMode = useWatch({ control, name: 'locationMode' });
  const quota = useWatch({ control, name: 'subscriptionQuota' });
  const quotaPeriod = useWatch({ control, name: 'subscriptionQuotaPeriod' });
  const slotCapacity = useWatch({ control, name: 'slotCapacity' }) ?? 1;

  const mode = saleModeOf({ isSubscription });
  const subscription = mode === SALE_MODE.SUBSCRIPTION;
  const schedulable = !!durationMinutes;

  const periodWord = quotaPeriod === QUOTA_PERIOD.WEEK ? 'semana' : 'mês';

  return (
    <View className="gap-4">
      <StepHeading
        title="Como entra na sua agenda"
        subtitle="Os horários saem da sua grade de atendimento. O cliente escolhe o que está livre e você aprova."
      />

      <StepCard>
        <DurationField
          control={control}
          setValue={setValue}
          allowNotSchedulable={mode === SALE_MODE.SINGLE}
        />
        {schedulable ? (
          <ControlledStepperRow
            control={control}
            name="slotCapacity"
            title="Vagas por horário"
            note={
              slotCapacity > 1
                ? `Turma de até ${slotCapacity} pessoas no mesmo horário.`
                : 'Atendimento individual.'
            }
            min={1}
            testID="service-wizard-slot-capacity"
          />
        ) : null}
      </StepCard>

      {subscription ? (
        <StepCard>
          <ControlledStepperRow
            control={control}
            name="subscriptionQuota"
            title="Sessões incluídas"
            note={
              quota && quotaPeriod
                ? `Até ${quota} ${quota > 1 ? 'sessões' : 'sessão'} por ${periodWord}, sem acumular.`
                : 'Quantas vezes o cliente pode marcar no período.'
            }
            min={1}
            max={31}
            testID="service-wizard-quota"
          />
          <ControlledOptionRows
            control={control}
            name="subscriptionQuotaPeriod"
            title="A cota renova"
            options={QUOTA_PERIOD_OPTIONS}
            testID="service-wizard-quota-period"
          />
        </StepCard>
      ) : null}

      <StepCard>
        <ControlledOptionRows
          control={control}
          name="locationMode"
          title="Onde acontece"
          options={LOCATION_MODE_OPTIONS}
          testID="service-wizard-location-mode"
        />
        {locationMode === LOCATION_MODE.PROVIDER_SPACE ? (
          <ServiceAddressField control={control} />
        ) : null}
      </StepCard>
    </View>
  );
}

/* ──────────────────────────  4. publicação  ──────────────────────────── */

export function StepPublish({
  control,
  footer,
  catalogNames = [],
  onPickCatalogName,
  identityLocked = false,
}: StepProps & {
  /** Extras da tela (ex.: salvar como modelo). */
  footer?: ReactNode;
  /**
   * Serviço da equipe que não é meu: o texto vale para todos que o fazem, então
   * só quem criou o item (ou o dono) muda. Preço e agenda continuam meus.
   */
  identityLocked?: boolean;
  /**
   * Em equipe, o que o negócio já vende. Repetir o nome de um item junta a
   * cópia nova ao mesmo card da página pública — em vez de criar um serviço
   * com o mesmo nome do colega.
   */
  catalogNames?: string[];
  onPickCatalogName?: (name: string) => void;
}) {
  return (
    <View className="gap-4">
      <StepHeading
        title="Como ele aparece"
        subtitle="É o que o cliente lê antes de contratar. Confira a prévia no rodapé."
      />

      {catalogNames.length > 0 && onPickCatalogName ? (
        <StepCard>
          <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
            A equipe já vende
          </ThemedText>
          <ThemedText className="text-xs text-neutral-400">
            Toque para fazer o mesmo serviço: vocês dividem o card, cada um com o seu preço.
          </ThemedText>
          <View className="flex-row flex-wrap gap-2">
            {catalogNames.map((catalogName) => (
              <PressableScale
                key={catalogName}
                haptic
                onPress={() => onPickCatalogName(catalogName)}
                accessibilityRole="button"
                className="rounded-full border border-neutral-200 bg-card px-3 py-1.5"
              >
                <ThemedText className="text-xs text-neutral-700">{catalogName}</ThemedText>
              </PressableScale>
            ))}
          </View>
        </StepCard>
      ) : null}

      <StepCard>
        {identityLocked ? (
          <ThemedText className="text-xs text-neutral-400">
            Este serviço é da equipe: o nome e a descrição são de quem o criou. O preço, a duração e
            a agenda abaixo são seus.
          </ThemedText>
        ) : null}
        <ControlledInput
          control={control}
          name="name"
          label="Nome"
          placeholder="Ex: Corte simples"
          disabled={identityLocked}
          testID="service-form-name-input"
        />
        <ControlledInput
          control={control}
          name="description"
          label="Descrição"
          placeholder="Ex: Aulas de 1 hora, material incluso..."
          multiline
          numberOfLines={4}
          disabled={identityLocked}
          testID="service-form-description-input"
        />
        <ControlledSwitchRow
          control={control}
          name="isActive"
          title="Serviço ativo"
          note="Desativado some do catálogo e da agenda, sem apagar nada."
          testID="service-wizard-active"
        />
      </StepCard>

      {footer}
    </View>
  );
}
